import React, { forwardRef, memo } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { m } from "framer-motion";
import { fadeIn, fadeInRight } from "../../../../utils/GlobalAnimations";
import { useSelector } from "react-redux";
import { selectCurrentLanguage } from "../../../store/slices/languageSlice";
import Utils from "../../../../utils/Utils";
import Buttons from "../../button/Buttons";

const BannerSplit = forwardRef((props, ref) => {
  const { items, parent } = props;
  const lang = useSelector(selectCurrentLanguage);
  const cl = Utils.isValidJson(parent.className)
    ? JSON.parse(parent.className)
    : parent.className;

  const [card1, card2] = items;

  const largeImage = parent?.image_main
    ? Utils.formatURL(
        `posts/${parent?.post_content_id}/${parent.image_main}?s=lg`
      )
    : undefined;

  const photoOf = (item) =>
    item?.content?.image_main
      ? Utils.formatURL(
          `posts/${item?.post_content_id}/${item.content.image_main}?s=md`
        )
      : undefined;

  return (
    <section
      ref={ref}
      className="bg-lightgray"
      style={{ ...cl }}
    >
      <Container fluid>
        <Row>
          {/* Large left image */}
          <m.div
            className="bg-cover bg-center md:h-[450px] xs:h-[300px] col-xl-6 col-lg-4"
            style={{ backgroundImage: largeImage ? `url("${largeImage}")` : undefined }}
            {...fadeInRight}
          />

          {/* Right 2×2 grid */}
          <Col xl={6} lg={8}>
            <Row>
              {/* order-1 / order-sm-1 — standalone photo (paired with card1) */}
              <m.div
                className="bg-cover bg-center xs:h-[300px] order-1 col-sm-6 order-sm-1"
                style={{ backgroundImage: card1 && photoOf(card1) ? `url("${photoOf(card1)}")` : undefined }}
                {...{ ...fadeIn, transition: { delay: 0.4 } }}
              />

              {/* order-2 / order-sm-2 — content card 1 */}
              {card1 && (
                <m.div
                  className="col-12 bg-[#f9f6f3] order-2 col-sm-6 order-sm-2"
                  {...{ ...fadeIn, transition: { delay: 0.5 } }}
                >
                  <div className="px-28 py-32 xl:px-12 xl:py-16 md:py-24">
                    {card1?.title_lang?.[lang] && (
                      <span className="font-serif font-semibold text-darkgray text-lg mb-[20px] block md:text-xmd md:leading-[22px]">
                        {card1.title_lang[lang]}
                      </span>
                    )}
                    {card1?.content?.description_lang?.[lang] && (
                      <p className="mb-[30px]">
                        {card1.content.description_lang[lang]}
                      </p>
                    )}
                    {card1?.content?.link?.[lang] && (
                      <Buttons
                        ariaLabel="banner split btn"
                        href={card1.content.link[lang]}
                        className="btn-fill font-medium font-serif uppercase rounded-none"
                        themeColor="#bf8c4c"
                        color="#fff"
                        size="sm"
                        title={card1?.content?.link_title_lang?.[lang] || ""}
                      />
                    )}
                  </div>
                </m.div>
              )}

              {/* order-4 / order-sm-3 — content card 2 */}
              {card2 && (
                <m.div
                  className="col-12 bg-[#f9f6f3] order-4 col-sm-6 order-sm-3"
                  {...{ ...fadeIn, transition: { delay: 0.8 } }}
                >
                  <div className="px-28 py-32 xl:px-12 xl:py-16 md:py-24">
                    {card2?.title_lang?.[lang] && (
                      <span className="font-serif font-semibold text-darkgray text-lg mb-[20px] block md:text-xmd md:leading-[22px]">
                        {card2.title_lang[lang]}
                      </span>
                    )}
                    {card2?.content?.description_lang?.[lang] && (
                      <p className="mb-[30px]">
                        {card2.content.description_lang[lang]}
                      </p>
                    )}
                    {card2?.content?.link?.[lang] && (
                      <Buttons
                        ariaLabel="banner split btn"
                        href={card2.content.link[lang]}
                        className="btn-fill font-medium font-serif uppercase rounded-none"
                        themeColor="#bf8c4c"
                        color="#fff"
                        size="sm"
                        title={card2?.content?.link_title_lang?.[lang] || ""}
                      />
                    )}
                  </div>
                </m.div>
              )}

              {/* order-3 / order-sm-4 — standalone photo (paired with card2) */}
              <m.div
                className="bg-cover bg-center xs:h-[300px] order-3 col-sm-6 order-sm-4"
                style={{ backgroundImage: card2 && photoOf(card2) ? `url("${photoOf(card2)}")` : undefined }}
                {...{ ...fadeIn, transition: { delay: 0.7 } }}
              />
            </Row>
          </Col>
        </Row>
      </Container>
    </section>
  );
});

export default memo(BannerSplit);
